import * as React from 'react';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { Popover } from '@tranquility-ai/theme-components/popover';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Button } from '@tranquility-ai/theme-components/button';
import { useOptionalI18n } from '@/i18n/LocaleProvider';
import { DEFAULT_APP_LOCALE } from '@/i18n/locales';
import {
  ALL_PROMPT_KEYWORDS,
  LIBRARY_PROMPTS,
  SUGGESTED_CATEGORY_ID,
  enabledLibraryPrompts,
  filterLibraryPrompts,
  libraryKeywordValues,
  type LibraryPromptRef,
} from '@/i18n/promptCatalog';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import '@tranquility-ai/theme-components/popover.css';
import {
  LightbulbFilamentIcon,
  XIcon,
  StarIcon,
  SealQuestionIcon,
  ArrowUpIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import './prompt-library.css';

type ResolvedPrompt = {
  id: string;
  categoryId: string;
  subCategoryId: string;
  category: string;
  subCategory: string;
  shortPrompt: string;
  prompt: string;
  purpose: string;
};

function resolveLibraryPrompt(entry: LibraryPromptRef, t: TranslateFn): ResolvedPrompt {
  const resolved: ResolvedPrompt = {
    id: entry.id,
    categoryId: entry.category,
    subCategoryId: entry.subCategory,
    category: t(`prompts.category.${entry.category}`),
    subCategory: t(`prompts.subCategory.${entry.subCategory}`),
    shortPrompt: '',
    prompt: '',
    purpose: '',
  };
  if (entry.suggestedId) {
    resolved.shortPrompt = t(`prompts.suggested.${entry.suggestedId}.label`);
    resolved.prompt = t(`prompts.suggested.${entry.suggestedId}.query`);
    resolved.purpose = resolved.subCategory;
    return resolved;
  }
  resolved.shortPrompt = t(`prompts.library.${entry.id}.short`);
  resolved.prompt = t(`prompts.library.${entry.id}.prompt`);
  resolved.purpose = t(`prompts.library.${entry.id}.purpose`);
  return resolved;
}

interface KeywordOption {
  value: string;
  label: string;
}

/**
 * In-modal keyword picker. Combobox portals its listbox to `document.body`,
 * which Safari treats as outside the Prompt Library `aria-modal` dialog — the
 * click is delivered as a backdrop dismiss and the library closes (TR-2648).
 * Popover already portals into the enclosing modal, so the list stays inside
 * the dialog for hit-testing, focus, and outside-click guards.
 */
function KeywordFilter({
  id,
  value,
  options,
  onValueChange,
}: {
  id: string;
  value: string;
  options: KeywordOption[];
  onValueChange: (value: string) => void;
}) {
  const t = useTimePilotT();
  const [isOpen, setIsOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLUListElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionRefs = React.useRef<(HTMLLIElement | null)[]>([]);
  const inputId = `${id}-input`;
  const listId = `${id}-listbox`;
  const selected = options.find((option) => option.value === value);
  const displayLabel = (selected?.label ?? value).replace(/^\u00a0+/, '');
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const normalizedFilter = filterQuery.trim().toLowerCase();
  const filteredOptions = React.useMemo(() => {
    if (!normalizedFilter) return options;
    return options.filter((option) => {
      const label = option.label.replace(/^\u00a0+/, '').toLowerCase();
      return (
        label.includes(normalizedFilter) || option.value.toLowerCase().includes(normalizedFilter)
      );
    });
  }, [options, normalizedFilter]);
  const lastIndex = Math.max(0, filteredOptions.length - 1);
  const activeOptionId =
    isOpen && filteredOptions[activeIndex] ? `${listId}-opt-${activeIndex}` : undefined;

  const selectIndex = React.useCallback(
    (index: number) => {
      const option = filteredOptions[index];
      if (!option) return;
      onValueChange(option.value);
      setFilterQuery('');
      setIsOpen(false);
    },
    [filteredOptions, onValueChange],
  );

  const openMenu = (index = selectedIndex) => {
    const visibleIndex = Math.max(
      0,
      filteredOptions.findIndex((option) => option.value === value),
    );
    setActiveIndex(Math.max(0, Math.min(lastIndex, visibleIndex >= 0 ? visibleIndex : index)));
    setIsOpen(true);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setFilterQuery('');
      return;
    }
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    setActiveIndex((current) => Math.max(0, Math.min(lastIndex, current)));
  }, [isOpen, lastIndex, normalizedFilter]);

  React.useEffect(() => {
    if (!isOpen) return;
    const menu = listRef.current;
    const option = optionRefs.current[activeIndex];
    if (!menu || !option) return;
    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;
    if (optionTop < menu.scrollTop) {
      menu.scrollTop = optionTop;
    } else if (optionBottom > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = optionBottom - menu.clientHeight;
    }
  }, [isOpen, activeIndex]);

  const moveActive = (nextIndex: number) => {
    if (filteredOptions.length === 0) return;
    setActiveIndex(Math.max(0, Math.min(lastIndex, nextIndex)));
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) openMenu();
        else moveActive(activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) openMenu();
        else moveActive(activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) openMenu(0);
        else moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) openMenu(lastIndex);
        else moveActive(lastIndex);
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(false);
        }
        break;
      default:
        break;
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        moveActive(activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        moveActive(activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        moveActive(lastIndex);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        selectIndex(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      variant="inverted"
      placement="bottom-start"
      sideOffset={4}
      showArrow={false}
      maxWidth="16rem"
      content={
        <div className="flex flex-col gap-1 p-1">
          <input
            ref={searchInputRef}
            type="search"
            value={filterQuery}
            onChange={(event) => {
              event.stopPropagation();
              setFilterQuery(event.target.value);
              setActiveIndex(0);
            }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                moveActive(activeIndex + 1);
                listRef.current?.focus();
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                moveActive(activeIndex - 1);
                listRef.current?.focus();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen(false);
              }
            }}
            placeholder={t('promptLibrary.searchKeywords')}
            aria-label={t('promptLibrary.searchKeywords')}
            autoComplete="off"
            className="w-full rounded-md border border-solid border-[var(--tp-popover-border)] bg-[var(--tp-popover-bg)] px-2 py-1.5 text-sm text-[var(--tp-popover-fg)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-brand,#0387fe)]"
          />
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={0}
            aria-label={t('promptLibrary.filterByKeyword')}
            aria-activedescendant={activeOptionId}
            className="custom-scrollbar3 m-0 max-h-80 list-none overflow-y-auto p-0"
            onKeyDown={handleListKeyDown}
          >
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-[var(--tp-popover-fg-muted,var(--text-muted))]">
                {t('promptLibrary.noKeywords')}
              </li>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  // Keyboard interaction is handled on the listbox (WAI-ARIA
                  // combobox / activedescendant). Options are not tab stops.
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <li
                    key={option.value}
                    id={`${listId}-opt-${index}`}
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-left text-sm ${
                      isSelected
                        ? 'bg-brand-blue-light text-white'
                        : isActive
                          ? 'bg-[var(--tp-popover-control-hover-bg)] text-[var(--tp-popover-fg)]'
                          : 'bg-transparent text-[var(--tp-popover-fg)]'
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      selectIndex(index);
                    }}
                  >
                    {option.label}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      }
    >
      <button
        type="button"
        id={inputId}
        role="combobox"
        aria-label={t('promptLibrary.filterByKeyword')}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-activedescendant={activeOptionId}
        className="flex h-[38px] w-[200px] items-center justify-between gap-1 rounded-lg border border-solid border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none hover:border-gray-400 focus-visible:border-gray-400"
        onClick={(event) => {
          event.stopPropagation();
          if (isOpen) setIsOpen(false);
          else openMenu();
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="min-w-0 flex-1 truncate text-left">{displayLabel}</span>
        <CaretDownIcon size={18} weight="bold" aria-hidden className="shrink-0 opacity-90" />
      </button>
    </Popover>
  );
}

function PromptFullPromptPopover({
  prompt,
  isOpen,
  onOpenChange,
}: {
  prompt: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTimePilotT();
  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      variant="inverted"
      placement="top"
      sideOffset={5}
      maxWidth="20rem"
      title={t('promptLibrary.fullPrompt')}
      content={<p className="m-0 text-sm text-[var(--tp-popover-fg)]">{prompt}</p>}
    >
      <Button
        type="button"
        iconOnly
        size="sm"
        style="text"
        color="subdued"
        background="default"
        aria-label={t('promptLibrary.viewFull')}
        aria-haspopup="dialog"
        className="flex-shrink-0"
        onClick={(event) => {
          event.stopPropagation();
          onOpenChange(!isOpen);
        }}
      >
        <SealQuestionIcon
          size={20}
          weight="fill"
          className="text-gray-400 hover:text-brand-blue"
        />
      </Button>
    </Popover>
  );
}

export interface PromptLibraryProps {
  featureFlags?: { [key: string]: any };
  onPromptSelect: (query: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  triggerElement?: React.ReactNode;
  className?: string;
  popoverRef?: React.RefObject<HTMLDivElement>;
  isOpen?: boolean;
}

export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  featureFlags,
  onPromptSelect,
  onOpenChange,
  triggerElement,
  className = '',
  popoverRef,
  isOpen: externalIsOpen,
}) => {
  const t = useTimePilotT();
  const locale = useOptionalI18n()?.locale ?? DEFAULT_APP_LOCALE;
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const [selectedCategory, setSelectedCategory] = React.useState<string>(ALL_PROMPT_KEYWORDS);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [openFullPromptKey, setOpenFullPromptKey] = React.useState<string | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const setIsOpen = React.useCallback(
    (open: boolean) => {
      if (externalIsOpen !== undefined) {
        onOpenChange?.(open);
      } else {
        setInternalIsOpen(open);
      }
    },
    [externalIsOpen, onOpenChange],
  );

  React.useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedCategory(ALL_PROMPT_KEYWORDS);
      setOpenFullPromptKey(null);
      return;
    }

    // Modal focus lands on the close button first; move focus to search once the
    // dialog has mounted so typing filters immediately without an extra click.
    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      });
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [isOpen]);

  const enabledEntries = React.useMemo(
    () => enabledLibraryPrompts(LIBRARY_PROMPTS, featureFlags?.promptLibraryPrompts),
    [featureFlags?.promptLibraryPrompts],
  );

  const prompts = React.useMemo(
    () => enabledEntries.map((entry) => resolveLibraryPrompt(entry, t)),
    [enabledEntries, t],
  );

  const categoryOptions = React.useMemo((): KeywordOption[] => {
    return libraryKeywordValues(enabledEntries).map((value) => {
      if (value === ALL_PROMPT_KEYWORDS) {
        return { value, label: t('promptLibrary.allKeywords') };
      }
      const separator = value.indexOf(' > ');
      if (separator === -1) {
        return { value, label: t(`prompts.category.${value}`) };
      }
      const subCategory = value.slice(separator + 3);
      return {
        value,
        label: `\u00a0\u00a0\u00a0\u00a0${t(`prompts.subCategory.${subCategory}`)}`,
      };
    });
  }, [enabledEntries, t]);

  React.useEffect(() => {
    if (!categoryOptions.some((option) => option.value === selectedCategory)) {
      setSelectedCategory(ALL_PROMPT_KEYWORDS);
    }
  }, [categoryOptions, selectedCategory]);

  const filteredPrompts = React.useMemo(() => {
    const selectedIds = new Set(
      filterLibraryPrompts(enabledEntries, selectedCategory).map((entry) => entry.id),
    );
    let filtered = prompts.filter((prompt) => selectedIds.has(prompt.id));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (prompt) =>
          prompt.shortPrompt.toLowerCase().includes(query) ||
          prompt.prompt.toLowerCase().includes(query) ||
          prompt.category.toLowerCase().includes(query) ||
          prompt.subCategory.toLowerCase().includes(query),
      );
    }

    filtered = [...filtered].sort((a, b) => {
      const comparison = a.shortPrompt.localeCompare(b.shortPrompt, locale);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [enabledEntries, locale, prompts, selectedCategory, sortDirection, searchQuery]);

  const handlePromptClick = (query: string) => {
    onPromptSelect(query);
    setIsOpen(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory(ALL_PROMPT_KEYWORDS);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const defaultTrigger = (
    <Button
      type="button"
      id="prompt-library-open-button"
      aria-label={t('promptLibrary.title')}
      aria-haspopup="dialog"
      size="sm"
      style="outline"
      color="light"
      background="inverse"
      leadingIcon={<LightbulbFilamentIcon weight="duotone" className="text-white/65" />}
      className="rounded-full border-brand-blue-light/60 hover:border-brand-blue-light/100 bg-white/5"
      onClick={() => setIsOpen(true)}
    >
      {t('promptLibrary.title')}
    </Button>
  );

  const trigger = triggerElement ? (
    <span
      role="presentation"
      className={`inline-flex ${className}`.trim()}
      onClick={(event) => {
        event.stopPropagation();
        toggleOpen();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleOpen();
        }
      }}
    >
      {triggerElement}
    </span>
  ) : (
    defaultTrigger
  );

  return (
    <>
      {trigger}
      <Modal
        id="prompt-library-modal"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        title={t('promptLibrary.title')}
        size="xl"
        contentClassName="tp-prompt-library-modal"
        bodyClassName="flex min-h-0 max-h-[min(70vh,720px)] flex-col overflow-hidden bg-white p-0"
      >
        <div
          ref={popoverRef}
          className="tp-prompt-library flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-md bg-white p-4 text-gray-900"
        >
          <div
            className="flex flex-wrap items-center justify-center gap-2 rounded-lg bg-brand-blue-light/10 p-3 sm:gap-4 sm:p-4"
            data-question-bar-interactive
            onClick={(event) => event.stopPropagation()}
          >
            <span className="text-sm font-medium text-gray-700">{t('promptLibrary.search')}</span>
            <div className="relative flex flex-row">
              <input
                ref={searchInputRef}
                type="search"
                id="promt-library-search-input"
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label={t('promptLibrary.searchPrompts')}
                autoComplete="off"
                onClick={(event) => event.stopPropagation()}
                className="min-w-[200px] max-w-[200px] rounded-lg border border-solid border-gray-200 bg-white p-1.5 text-sm text-gray-900 outline-none focus:outline-none sm:p-1.5"
              />
              {searchQuery ? (
                <Button
                  type="button"
                  id="promt-library-search-clear-button"
                  iconOnly
                  size="sm"
                  style="text"
                  color="subdued"
                  background="default"
                  aria-label={t('promptLibrary.clearSearch')}
                  onClick={(event) => {
                    event.stopPropagation();
                    clearSearch();
                  }}
                  className="absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XIcon />
                </Button>
              ) : null}
            </div>
            <span className="hidden text-sm font-medium text-gray-700 sm:flex">{t('promptLibrary.filterLabel')}</span>
            <div
              id="promt-library-category-select"
              className="hidden min-w-[200px] sm:block"
              onClick={(event) => event.stopPropagation()}
            >
              <KeywordFilter
                id="promt-library-category-select"
                value={selectedCategory}
                options={categoryOptions}
                onValueChange={setSelectedCategory}
              />
            </div>
          </div>

          {filteredPrompts.length === 0 ? (
            <div className="w-full py-4 text-center text-sm text-gray-500">
              {prompts.length === 0
                ? t('promptLibrary.none')
                : searchQuery
                  ? t('promptLibrary.noMatchQuery', { query: searchQuery })
                  : t('promptLibrary.noMatchCategory', {
                      category: selectedCategory === ALL_PROMPT_KEYWORDS
                        ? t('promptLibrary.allKeywords')
                        : (categoryOptions.find((option) => option.value === selectedCategory)?.label ?? selectedCategory).replace(/^\u00a0+/, ''),
                    })}
            </div>
          ) : (
            <>
              <div
                className="tp-prompt-library-grid custom-scrollbar3 grid min-h-0 flex-1 isolate grid-cols-1 gap-4 overflow-x-hidden overflow-y-auto p-2 sm:grid-cols-2 md:grid-cols-3"
                onScroll={() => {
                  if (openFullPromptKey !== null) {
                    setOpenFullPromptKey(null);
                  }
                }}
              >
                {filteredPrompts.map((prompt) => {
                  const promptCardKey = prompt.id;
                  return (
                  <div
                    key={prompt.id}
                    onClick={(e) => {
                      if (!(e.target as Element).closest('.prompt-use-button')) {
                        handlePromptClick(prompt.prompt);
                      }
                    }}
                    className="group relative z-0 flex min-h-[140px] cursor-pointer flex-col items-start justify-between rounded-lg border-2 border-solid border-gray-200 bg-gray-100 p-3 text-left shadow-none transition-[border-color,background-color,box-shadow] duration-200 hover:z-10 hover:border-brand-blue-light/50 hover:bg-white hover:shadow-md"
                  >
                    <div className="flex w-full flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 flex-1 text-base font-semibold leading-tight tracking-normal text-gray-900 transition-colors duration-200">
                          {prompt.shortPrompt}
                        </span>
                        <PromptFullPromptPopover
                          prompt={prompt.prompt}
                          isOpen={openFullPromptKey === promptCardKey}
                          onOpenChange={(open) => {
                            setOpenFullPromptKey(open ? promptCardKey : null);
                          }}
                        />
                      </div>
                      <span className="line-clamp-2 text-sm italic text-gray-600">{prompt.prompt}</span>
                    </div>
                    <div className="flex w-full flex-row items-end justify-between gap-2">
                      <div className="flex flex-row items-center gap-2">
                        <div className="flex flex-row items-center justify-center gap-1 rounded-md bg-black/15 px-2 py-1 text-xs text-black">
                          {prompt.categoryId === SUGGESTED_CATEGORY_ID && (
                            <StarIcon size={12} weight="fill" className="text-brand-blue" />
                          )}
                          <span className="whitespace-nowrap text-xs text-black">{prompt.category}</span>
                        </div>
                        <span className="rounded-md bg-black/15 px-2 py-1 text-xs text-black">
                          {prompt.subCategory}
                        </span>
                      </div>
                      <TpTooltip
                        content={t('promptLibrary.usePrompt')}
                        placement="top"
                        delayDuration={1000}
                        sideOffset={5}
                      >
                        <Button
                          type="button"
                          iconOnly
                          size="sm"
                          style="solid"
                          color="accent"
                          background="default"
                          aria-label={t('promptLibrary.usePromptNamed', {
                            prompt: `${prompt.prompt.substring(0, 50)}${prompt.prompt.length > 50 ? '...' : ''}`,
                          })}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePromptClick(prompt.prompt);
                          }}
                          className="prompt-use-button shrink-0"
                        >
                          <ArrowUpIcon weight="bold" />
                        </Button>
                      </TpTooltip>
                    </div>
                  </div>
                  );
                })}
              </div>

              {(searchQuery || selectedCategory !== ALL_PROMPT_KEYWORDS) && (
                <div className="flex w-full justify-center pt-2">
                  <Button
                    type="button"
                    size="sm"
                    style="solid"
                    color="accent"
                    background="default"
                    onClick={resetFilters}
                    aria-label={t('promptLibrary.resetFilters')}
                  >
                    {t('promptLibrary.resetFiltersButton')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};
